import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { debounce } from 'lodash';
import apiClient from '../lib/axios';

interface RxNormDrug {
  rxcui: string;
  name: string;
  type: string;
}

interface PatientAllergy {
  id: string;
  rxcui: string | null;
  name: string;
  addedByDoctor: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface AllergyInputProps {
  patientId: string;
  allergies: PatientAllergy[];
  onAllergyAdded?: (allergy: PatientAllergy) => void;
  onAllergyRemoved?: (allergyId: string) => void;
  disabled?: boolean;
}

export const AllergyInput: React.FC<AllergyInputProps> = ({
  patientId,
  allergies,
  onAllergyAdded,
  onAllergyRemoved,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');

  // Debounced search term for API calls
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );

  // Search RxNorm API
  const { data: searchResults = [], isLoading: isSearching } = useQuery({
    queryKey: ['rxnorm-search', searchTerm],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2) return [];
      const { data } = await apiClient.get<RxNormDrug[]>('/patients/rxnorm/search', {
        params: { name: searchTerm },
      });
      return data;
    },
    enabled: searchTerm.length >= 2,
  });

  // Add allergy mutation
  const addMutation = useMutation({
    mutationFn: async (drug: { rxcui?: string; name: string }) => {
      const { data } = await apiClient.post<PatientAllergy>(
        `/patients/${patientId}/allergies`,
        drug
      );
      return data;
    },
    onSuccess: (newAllergy) => {
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      setInputValue('');
      setSearchTerm('');
      if (onAllergyAdded) {
        onAllergyAdded(newAllergy);
      }
    },
  });

  // Remove allergy mutation
  const removeMutation = useMutation({
    mutationFn: async (allergyId: string) => {
      await apiClient.delete(`/patients/${patientId}/allergies/${allergyId}`);
      return allergyId;
    },
    onSuccess: (allergyId) => {
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      if (onAllergyRemoved) {
        onAllergyRemoved(allergyId);
      }
    },
  });

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    setInputValue(value);
    debouncedSetSearch(value);
  };

  const handleSelectDrug = (_event: React.SyntheticEvent, value: RxNormDrug | string | null) => {
    if (!value) return;

    if (typeof value === 'string') {
      // Free text entry - user typed something not in the list
      addMutation.mutate({ name: value });
    } else {
      // Selected from RxNorm results
      addMutation.mutate({ rxcui: value.rxcui, name: value.name });
    }
  };

  const handleRemoveAllergy = (allergyId: string) => {
    removeMutation.mutate(allergyId);
  };

  return (
    <Box>
      {/* Allergy Chips */}
      {allergies.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {allergies.map((allergy) => (
            <Chip
              key={allergy.id}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {allergy.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Dr. {allergy.addedByDoctor.lastName}
                  </Typography>
                </Box>
              }
              onDelete={disabled ? undefined : () => handleRemoveAllergy(allergy.id)}
              deleteIcon={
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{
                height: 'auto',
                '& .MuiChip-label': {
                  display: 'block',
                  whiteSpace: 'normal',
                },
                '& .MuiChip-deleteIcon': {
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                  },
                },
              }}
              variant="outlined"
              disabled={removeMutation.isPending}
            />
          ))}
        </Box>
      )}

      {/* Search Input */}
      <Autocomplete
        freeSolo
        options={searchResults}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleSelectDrug}
        loading={isSearching}
        disabled={disabled || addMutation.isPending}
        filterOptions={(x) => x} // Don't filter, show all results from API
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('patients.allergies')}
            placeholder={t('patients.searchMedication')}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching || addMutation.isPending ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <li key={key} {...otherProps}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  RxCUI: {option.rxcui} | {option.type}
                </Typography>
              </Box>
            </li>
          );
        }}
        ListboxProps={{
          style: { maxHeight: 250 },
        }}
        noOptionsText={
          searchTerm.length < 2
            ? t('patients.typeToSearch')
            : t('patients.noMedicationsFound')
        }
      />

      {addMutation.isError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {t('patients.failedToAddAllergy')}
        </Typography>
      )}
    </Box>
  );
};
